import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy, Plus, PenSquare, Trash2, Clock, Target, Users,
  CheckCircle, Eye, EyeOff, ArrowLeft, Image, Upload
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (secs) => {
  if (!secs) return "–";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const emptyQuestion = () => ({
  question: "",
  options: ["", "", "", ""],
  correct_answer: "A",
  image_url: null,
});

// ── QuizForm ─────────────────────────────────────────────────────────────────
function QuizForm({ initial, courses, onSave, onCancel, saving }) {
  const [form, setForm] = useState(
    initial ?? {
      title: "",
      description: "",
      course_id: "",
      time_limit_minutes: 30,
      passing_score: 60,
      questions: [emptyQuestion()],
    }
  );

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Question helpers
  const setQ = (i, k, v) =>
    setForm((p) => {
      const qs = [...p.questions];
      qs[i] = { ...qs[i], [k]: v };
      return { ...p, questions: qs };
    });

  const setOption = (qi, oi, v) =>
    setForm((p) => {
      const qs = [...p.questions];
      const opts = [...qs[qi].options];
      opts[oi] = v;
      qs[qi] = { ...qs[qi], options: opts };
      return { ...p, questions: qs };
    });

  const addQuestion = () =>
    setForm((p) => ({ ...p, questions: [...p.questions, emptyQuestion()] }));

  const removeQuestion = (i) =>
    setForm((p) => ({
      ...p,
      questions: p.questions.filter((_, idx) => idx !== i),
    }));

  const handleImageUpload = async (e, qi) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Quick validation
    if (!file.type.startsWith("image/")) return toast.error("Must be an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be less than 5MB");
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("kind", "quiz_image");
    
    try {
      const res = await axios.post(`${API}/uploads`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setQ(qi, "image_url", res.data.url);
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error("Failed to upload image");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate
    if (!form.title.trim()) return toast.error("Quiz title is required");
    if (form.questions.length === 0) return toast.error("Add at least one question");
    for (let i = 0; i < form.questions.length; i++) {
      const q = form.questions[i];
      if (!q.question.trim()) return toast.error(`Question ${i + 1} text is required`);
      if (q.options.some((o) => !o.trim())) return toast.error(`All options in Q${i + 1} must be filled`);
    }
    onSave(form);
  };

  const optionLabels = ["A", "B", "C", "D"];

  return (
    <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-200">
      {/* Basic Info */}
      <div className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-5 space-y-4 shadow-xs">
        <h3 className="text-slate-900 dark:text-white font-outfit font-bold text-base flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" /> Basic Info
        </h3>
        <div>
          <Label className="text-slate-600 dark:text-slate-400 text-xs font-medium">Quiz Title *</Label>
          <Input
            required value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="e.g. Options Trading Fundamentals Quiz"
            className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1 rounded-xl focus:border-emerald-500"
          />
        </div>
        <div>
          <Label className="text-slate-600 dark:text-slate-400 text-xs font-medium">Description</Label>
          <Textarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Brief description of what this quiz covers..."
            className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1 min-h-[70px] text-sm rounded-xl focus:border-emerald-500"
          />
        </div>
      </div>

      {/* Audience Selection */}
      <div className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-5 space-y-3 shadow-xs">
        <h3 className="text-slate-900 dark:text-white font-outfit font-bold text-base flex items-center gap-2">
          <Users className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Who can take this quiz?
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">Select a course to limit the quiz to enrolled students only, or choose "All Students" to make it open to everyone.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-1">
          {/* All Students card */}
          <button
            type="button"
            onClick={() => set("course_id", "")}
            className={`text-left p-4 rounded-xl border-2 transition-all ${
              form.course_id === ""
                ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 shadow-xs"
                : "border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 hover:border-slate-300 dark:hover:border-slate-700"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-lg ${
                form.course_id === "" ? "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700" : "bg-slate-100 dark:bg-slate-800"
              }`}>
                🌐
              </div>
              <div>
                <p className={`text-sm font-semibold font-outfit ${
                  form.course_id === "" ? "text-emerald-700 dark:text-emerald-400" : "text-slate-900 dark:text-white"
                }`}>All Students</p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Every enrolled user</p>
              </div>
            </div>
            {form.course_id === "" && (
              <div className="mt-2.5 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">Selected</span>
              </div>
            )}
          </button>

          {/* Course cards */}
          {courses.map((c, i) => {
            const icons = ["📈", "📊", "💹", "🏆"];
            const icon = icons[i % icons.length];
            const selected = form.course_id === c.course_id;
            return (
              <button
                key={c.course_id}
                type="button"
                onClick={() => set("course_id", c.course_id)}
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  selected
                    ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 shadow-xs"
                    : "border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 hover:border-slate-300 dark:hover:border-slate-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-lg ${
                    selected ? "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700" : "bg-slate-100 dark:bg-slate-800"
                  }`}>
                    {icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold font-outfit truncate ${selected ? "text-emerald-700 dark:text-emerald-400" : "text-slate-900 dark:text-white"}`} title={c.title}>{c.title}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Enrolled students only</p>
                  </div>
                </div>
                {selected && (
                  <div className="mt-2.5 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">Selected</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time & Passing Score */}
      <div className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-5 shadow-xs">
        <h3 className="text-slate-900 dark:text-white font-outfit font-bold text-base flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-indigo-500" /> Quiz Settings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-600 dark:text-slate-400 text-xs font-medium">Time Limit (minutes)</Label>
            <Input
              type="number" min="1" max="180"
              value={form.time_limit_minutes}
              onChange={(e) => set("time_limit_minutes", parseInt(e.target.value) || 30)}
              className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1 rounded-xl focus:border-emerald-500"
            />
          </div>
          <div>
            <Label className="text-slate-600 dark:text-slate-400 text-xs font-medium">Passing Score (%)</Label>
            <Input
              type="number" min="1" max="100"
              value={form.passing_score}
              onChange={(e) => set("passing_score", parseInt(e.target.value) || 60)}
              className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1 rounded-xl focus:border-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-slate-900 dark:text-white font-outfit font-bold text-base">
            Questions ({form.questions.length})
          </h3>
          <Button
            type="button"
            onClick={addQuestion}
            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 h-8 text-xs font-medium rounded-xl shadow-xs"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Question
          </Button>
        </div>

        {form.questions.map((q, qi) => (
          <div key={qi} className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-5 space-y-4 shadow-xs">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <Label className="text-slate-600 dark:text-slate-400 text-xs font-medium">Q{qi + 1}. Question Text *</Label>
                <Textarea
                  value={q.question}
                  onChange={(e) => setQ(qi, "question", e.target.value)}
                  placeholder="Type your question here..."
                  className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1 min-h-[60px] text-sm rounded-xl focus:border-emerald-500"
                />
                
                {/* Image Upload for Question */}
                <div className="mt-3">
                  {q.image_url ? (
                    <div className="relative inline-block border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-slate-950">
                      <img src={q.image_url.startsWith('http') ? q.image_url : API.replace('/api', '') + q.image_url} alt="Question Attachment" className="max-h-32 object-contain" />
                      <button
                        type="button"
                        onClick={() => setQ(qi, "image_url", null)}
                        className="absolute top-1.5 right-1.5 w-6 h-6 bg-slate-900/80 hover:bg-rose-600 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor={`q-img-${qi}`} className="cursor-pointer inline-flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors bg-slate-100 dark:bg-slate-800/70 hover:bg-slate-200 dark:hover:bg-slate-800 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 font-medium">
                        <Upload className="w-3.5 h-3.5" /> Attach Image
                      </Label>
                      <input
                        id={`q-img-${qi}`}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, qi)}
                      />
                    </div>
                  )}
                </div>
              </div>
              {form.questions.length > 1 && (
                <button type="button" onClick={() => removeQuestion(qi)} className="mt-5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {q.options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setQ(qi, "correct_answer", optionLabels[oi])}
                    className={`w-7 h-7 rounded-lg text-xs font-bold font-jetbrains border transition-all flex-shrink-0 ${
                      q.correct_answer === optionLabels[oi]
                        ? "bg-emerald-600 border-emerald-600 text-white shadow-xs"
                        : "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-emerald-500"
                    }`}
                  >
                    {optionLabels[oi]}
                  </button>
                  <Input
                    value={opt}
                    onChange={(e) => setOption(qi, oi, e.target.value)}
                    placeholder={`Option ${optionLabels[oi]}`}
                    className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white text-sm h-8 flex-1 rounded-xl focus:border-emerald-500"
                  />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-sans">
              Click letter icon to mark correct answer. Currently selected: <span className="text-emerald-600 dark:text-emerald-400 font-bold">{q.correct_answer}</span>
            </p>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-xl">
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-xs min-w-[120px]">
          {saving ? "Saving..." : initial ? "Update Quiz" : "Create & Notify Students"}
        </Button>
      </div>
    </form>
  );
}

// ── QuizResults ───────────────────────────────────────────────────────────────
function QuizResults({ quiz, onBack }) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAttempts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/quizzes/${quiz.quiz_id}/attempts`, { withCredentials: true });
      setAttempts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Failed to load results");
    } finally {
      setLoading(false);
    }
  }, [quiz.quiz_id]);

  useEffect(() => { fetchAttempts(); }, [fetchAttempts]);

  const passed = attempts.filter((a) => a.passed);
  const failed = attempts.filter((a) => !a.passed);

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors p-1 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white">{quiz.title}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Results & Leaderboard</p>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Attempts", value: attempts.length, color: "text-slate-900 dark:text-white" },
          { label: "Passed", value: passed.length, color: "text-emerald-600 dark:text-emerald-400" },
          { label: "Failed", value: failed.length, color: "text-rose-600 dark:text-rose-400" },
          { label: "Pass Rate", value: attempts.length > 0 ? `${Math.round((passed.length / attempts.length) * 100)}%` : "–", color: "text-slate-900 dark:text-white" },
        ].map((s, i) => (
          <div key={i} className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-4 text-center shadow-xs">
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold font-outfit mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800/80 rounded-2xl overflow-hidden shadow-xs">
        <div className="bg-slate-50/70 dark:bg-slate-850 px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          <span className="text-slate-900 dark:text-white font-outfit font-semibold">Leaderboard</span>
          <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">Sorted by score → speed</span>
        </div>

        {loading ? (
          <div className="py-10 text-center"><div className="w-7 h-7 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : attempts.length === 0 ? (
          <div className="py-10 text-center text-slate-500 dark:text-slate-400 text-sm">No attempts yet</div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
            {attempts.map((a, i) => (
              <div key={a.attempt_id} className={`flex items-center gap-4 px-5 py-3.5 ${i === 0 ? "bg-amber-50/30 dark:bg-amber-950/10" : ""}`}>
                {/* Rank */}
                <div className={`w-8 text-center font-jetbrains font-bold text-sm ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-amber-700" : "text-slate-400"}`}>
                  {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                </div>
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 flex items-center justify-center text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                  {a.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900 dark:text-white font-medium truncate">{a.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{a.email}</p>
                </div>
                {/* Score */}
                <div className="text-right">
                  <p className={`text-sm font-bold font-jetbrains ${a.passed ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                    {a.score}%
                  </p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">{fmt(a.time_taken_seconds)}</p>
                </div>
                {/* Pass/Fail */}
                <div className="w-16 text-right">
                  {a.passed
                    ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 text-[10px]">Passed</Badge>
                    : <Badge className="bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800 text-[10px]">Failed</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main QuizManager ──────────────────────────────────────────────────────────
export default function QuizManager() {
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // 'list' | 'create' | 'edit' | 'results'
  const [selected, setSelected] = useState(null); // quiz being edited / viewed
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    // Load courses - try admin endpoint first, fall back to public one
    try {
      const cRes = await axios.get(`${API}/admin/courses`, { withCredentials: true });
      const raw = cRes.data;
      setCourses(Array.isArray(raw) ? raw : raw?.courses ?? []);
    } catch {
      try {
        const cRes = await axios.get(`${API}/courses`, { withCredentials: true });
        const raw = cRes.data;
        setCourses(Array.isArray(raw) ? raw : raw?.courses ?? []);
      } catch (e) {
        console.error("Failed to load courses:", e);
      }
    }

    // Load quizzes independently
    try {
      const qRes = await axios.get(`${API}/admin/quizzes`, { withCredentials: true });
      setQuizzes(Array.isArray(qRes.data) ? qRes.data : []);
    } catch (e) {
      console.error("Failed to load quizzes:", e.response?.data || e.message);
    }

    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await axios.post(`${API}/quizzes`, form, { withCredentials: true });
      // Auto-broadcast notification
      try {
        await axios.post(`${API}/admin/messages`, {
          title: `🏆 New Quiz: ${form.title}`,
          message: `A new quiz competition is live! "${form.title}" — ${form.questions.length} questions, ${form.time_limit_minutes} min time limit. Take it now!`,
          type: "announcement",
          audience: "all",
        }, { withCredentials: true });
      } catch (_) { /* broadcast failure is non-critical */ }
      toast.success("Quiz created & students notified!");
      setView("list");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create quiz");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      await axios.put(`${API}/quizzes/${selected.quiz_id}`, form, { withCredentials: true });
      toast.success("Quiz updated!");
      setView("list");
      setSelected(null);
      fetchData();
    } catch (e) {
      toast.error("Failed to update quiz");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (quiz) => {
    if (!window.confirm(`Delete "${quiz.title}"? This will also delete all student attempts.`)) return;
    try {
      await axios.delete(`${API}/quizzes/${quiz.quiz_id}`, { withCredentials: true });
      toast.success("Quiz deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete quiz");
    }
  };

  const toggleActive = async (quiz) => {
    try {
      await axios.put(`${API}/quizzes/${quiz.quiz_id}`, { ...quiz, is_active: !quiz.is_active }, { withCredentials: true });
      toast.success(quiz.is_active ? "Quiz hidden from students" : "Quiz published to students");
      fetchData();
    } catch (e) {
      toast.error("Failed to update quiz");
    }
  };

  // ── Views ──────────────────────────────────────────────────────────────────
  if (view === "results" && selected) {
    return <QuizResults quiz={selected} onBack={() => { setView("list"); setSelected(null); }} />;
  }

  if (view === "create") {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setView("list")} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors p-1 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Create New Quiz
          </h2>
        </div>
        <QuizForm courses={courses} onSave={handleCreate} onCancel={() => setView("list")} saving={saving} />
      </div>
    );
  }

  if (view === "edit" && selected) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => { setView("list"); setSelected(null); }} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors p-1 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <PenSquare className="w-5 h-5 text-indigo-500" /> Edit Quiz
          </h2>
        </div>
        <QuizForm initial={selected} courses={courses} onSave={handleUpdate} onCancel={() => { setView("list"); setSelected(null); }} saving={saving} />
      </div>
    );
  }

  // ── List View ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Quiz & Competitions
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Create quizzes, set questions, track student performance</p>
        </div>
        <Button onClick={() => setView("create")} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-xs h-9">
          <Plus className="w-4 h-4 mr-2" /> Create Quiz
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: "Total Quizzes", value: quizzes.length, color: "text-slate-900 dark:text-white" },
          { label: "Published", value: quizzes.filter((q) => q.is_active).length, color: "text-emerald-600 dark:text-emerald-400" },
          { label: "Hidden", value: quizzes.filter((q) => !q.is_active).length, color: "text-slate-500 dark:text-slate-400" },
        ].map((s, i) => (
          <div key={i} className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800/80 rounded-2xl p-4 text-center shadow-xs">
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold font-outfit mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Quiz Cards */}
      {loading ? (
        <div className="py-16 text-center"><div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : quizzes.length === 0 ? (
        <div className="py-16 text-center bg-white dark:bg-slate-900/40 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
          <Trophy className="w-10 h-10 text-slate-400 dark:text-slate-500 mx-auto mb-3" />
          <p className="text-slate-600 dark:text-slate-400">No quizzes yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {quizzes.map((quiz) => (
            <div key={quiz.quiz_id} className={`bg-white dark:bg-slate-900/70 border rounded-2xl p-5 shadow-xs transition-all ${quiz.is_active ? "border-slate-200 dark:border-slate-800/80" : "border-slate-200/50 dark:border-slate-800/40 opacity-70"}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <h3 className="text-slate-900 dark:text-white font-outfit font-semibold text-base">{quiz.title}</h3>
                    <Badge className={`border text-[10px] font-medium ${quiz.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800" : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"}`}>
                      {quiz.is_active ? "Published" : "Hidden"}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-1">{quiz.description}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-2.5 text-xs text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-indigo-500" /> {quiz.time_limit_minutes} min</span>
                    <span className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-purple-500" /> Pass: {quiz.passing_score}%</span>
                    <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> {quiz.questions?.length || 0} Questions</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => { setSelected(quiz); setView("results"); }}
                    className="h-8 px-3 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 text-xs rounded-xl"
                  >
                    <Users className="w-3.5 h-3.5 mr-1.5" /> Results
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => toggleActive(quiz)}
                    className="h-8 px-3 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-xs rounded-xl"
                  >
                    {quiz.is_active ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                    {quiz.is_active ? "Hide" : "Publish"}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => { setSelected(quiz); setView("edit"); }}
                    className="h-8 px-2 text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl"
                    title="Edit Quiz"
                  >
                    <PenSquare className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => handleDelete(quiz)}
                    className="h-8 px-2 text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 rounded-xl"
                    title="Delete Quiz"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
